/* eslint-disable multiline-ternary */
import { Button, Flex, Grid, Progress, useToast } from '@chakra-ui/react'
import Header from '../../../common/components/Header'
import { useEffect, useState } from 'react'
import InputField from '../../../../../components/common/form/InputField'
import { Form, useFormState } from 'react-final-form'
import { CLINIC_SPECIALIZATION_OPTIONS } from './constants'
import SelectField from '../../../../../components/common/form/SelectField'
import { getDoctorsService } from '../../../../../services/Services'
import { useNavigate } from 'react-router-dom'
import useDoctorProfile from '../../../common/hooks/useDoctorProfile'
import { useIntl } from 'react-intl'
import messages from './messages'
import { useRole } from '../../../../../providers/RoleProvider'
import InternalStandaloneLayout from '../../../../../components/InternalStandaloneLayout'

interface Props {
  onNext: () => void
}

const FirstStep = ({ onNext }: Props) => {
  const { formatMessage } = useIntl()
  return (
    <Flex flexDir="column" gap={8} textAlign="center">
      <Header>{formatMessage(messages.welcome)}</Header>
      <Flex justifyContent="center">
        <Button colorScheme="gray" onClick={onNext}>
          {formatMessage(messages.getStarted)}
        </Button>
      </Flex>
    </Flex>
  )
}

const SecondStep = ({ onNext }: Props) => {
  const { formatMessage } = useIntl()
  const { values } = useFormState()
  const { firstName, lastName, phone, address, city } = values
  const isFilled = firstName && lastName && phone && address && city

  return (
    <Flex flexDir="column" gap={8}>
      <Header>{formatMessage(messages.personalInfo)}</Header>
      <Grid gap={4} templateColumns={{ base: '1fr', sm: '1fr 1fr' }}>
        <InputField
          name="firstName"
          label={formatMessage(messages.firstName)}
        />
        <InputField name="lastName" label={formatMessage(messages.lastName)} />
      </Grid>
      <InputField name="phone" label={formatMessage(messages.phone)} />
      <Grid gap={4} templateColumns={{ base: '1fr', sm: '1fr 1fr' }}>
        <InputField name="address" label={formatMessage(messages.address)} />
        <InputField name="city" label={formatMessage(messages.city)} />
      </Grid>
      <Flex justifyContent="center">
        <Button colorScheme="gray" onClick={onNext} isDisabled={!isFilled}>
          {formatMessage(messages.next)}
        </Button>
      </Flex>
    </Flex>
  )
}

const ThirdStep = ({ onNext }: Props) => {
  const { formatMessage } = useIntl()
  const { values } = useFormState()
  const { visitPrice, followUpPrice } = values
  const isFilled = visitPrice && followUpPrice

  return (
    <Flex flexDir="column" gap={8}>
      <Header>{formatMessage(messages.paymentInfo)}</Header>
      <InputField
        type="number"
        name="visitPrice"
        label={formatMessage(messages.visitPrice)}
      />
      <InputField
        type="number"
        name="followUpPrice"
        label={formatMessage(messages.followUpPrice)}
      />
      <Flex justifyContent="center">
        <Button colorScheme="gray" onClick={onNext} isDisabled={!isFilled}>
          {formatMessage(messages.next)}
        </Button>
      </Flex>
    </Flex>
  )
}

const FourthStep = () => {
  const { formatMessage } = useIntl()
  const { values } = useFormState()
  const { schemaId } = values
  const isFilled = !!schemaId

  return (
    <Flex flexDir="column" gap={8}>
      <Header>{formatMessage(messages.clinicInfo)}</Header>
      <SelectField
        name="schemaId"
        options={CLINIC_SPECIALIZATION_OPTIONS}
        label={formatMessage(messages.clinicSpecialization)}
      />
      <Flex justifyContent="center">
        <Button colorScheme="gray" type="submit" isDisabled={!isFilled}>
          {formatMessage(messages.submit)}
        </Button>
      </Flex>
    </Flex>
  )
}

export default function Onboarding() {
  const [step, setStep] = useState(0)
  const toast = useToast()
  const { doctorId } = useRole()
  const navigate = useNavigate()
  const { formatMessage } = useIntl()

  const doctorProfile = useDoctorProfile({ ownerId: doctorId })

  useEffect(() => {
    if (doctorProfile.isOnboarded) {
      toast({
        title: formatMessage(messages.alreadyOnboarded),
        description: formatMessage(messages.alreadyOnboardedDescription),
        status: 'info',
        duration: 4000,
        isClosable: true
      })
      navigate('/dashboard')
    }
  })

  const handleNext = () => {
    setStep((prev) => prev + 1)
  }

  const handleDoctorProfileUpdate = async (values: any) => {
    if (!doctorId) return
    await getDoctorsService().onboardDoctor(values)
    toast({
      title: formatMessage(messages.success),
      description: formatMessage(messages.successDescription),
      status: 'success',
      duration: 4000,
      isClosable: true
    })
    navigate('/dashboard')
  }

  const renderStep = () => {
    switch (step) {
      case 0:
        return <FirstStep onNext={handleNext} />
      case 1:
        return <SecondStep onNext={handleNext} />
      case 2:
        return <ThirdStep onNext={handleNext} />
      case 3:
        return <FourthStep />
      default:
        return <FirstStep onNext={handleNext} />
    }
  }

  return (
    <InternalStandaloneLayout
      eyebrow={formatMessage({
        id: 'onboarding.step',
        defaultMessage: 'Account setup'
      })}
      maxW="560px"
    >
      <Form
        onSubmit={handleDoctorProfileUpdate}
        render={({ handleSubmit, values }) => {
          const {
            firstName,
            lastName,
            phone,
            address,
            city,
            visitPrice,
            followUpPrice
          } = values
          const stepValidity = [
            true,
            firstName && lastName && phone && address && city,
            visitPrice && followUpPrice,
            true
          ]

          const handleFormKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && step < 3 && stepValidity[step]) {
              e.preventDefault()
              handleNext()
            }
          }

          return (
            <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
              {step !== 0 ? (
                <Progress
                  size="xs"
                  mb={6}
                  borderRadius="full"
                  colorScheme="gray"
                  value={(step / 3) * 100 - 10}
                />
              ) : null}
              {renderStep()}
            </form>
          )
        }}
      />
    </InternalStandaloneLayout>
  )
}
